import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { queryClient } from "@/lib/query-client";
import { NotFoundPage } from "@/components/not-found-page";
import { routeTree } from "./routeTree.gen";
import "@/styles/globals.css";

// Register the service worker in production only. The SW provides offline
// caching for the app shell + API responses (jobs, alerts, setup state).
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Registration failures are non-fatal — the app works fine without SW.
    });
  });
}

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        richColors
        position="top-center"
        toastOptions={{
          // Larger tap targets for mobile — toasts often carry action buttons
          // ("Install", "Got it") that were too small to tap reliably. 44px
          // is iOS HIG minimum tap target.
          classNames: {
            toast: "p-4 gap-3 min-h-[64px]",
            actionButton: "px-4 py-2 min-h-[44px] text-sm font-medium",
            cancelButton: "px-4 py-2 min-h-[44px] text-sm font-medium",
          },
        }}
      />
    </QueryClientProvider>
  </StrictMode>
);
